import { resolveValue } from 'components/form/final-form/useField'
import createDecorator, { Calculation } from 'final-form-calculate'
import { Procedimento } from 'graphql/types.generated'
import { ProcedimentosAutomaticosEscutaInicial } from 'types/enums'
import { MetaArray, MetaPath } from 'util/metaPath'
import { v4 as uuidv4 } from 'uuid'

import { createMedicoesEscutaCalculations } from '../components/MedicoesPanel/calculator'
import { MedicoesPanelModel } from '../components/MedicoesPanel/MedicoesPanel'
import { createAgendarConsultaCalculations } from '../detail/soap/desfecho/calculator/calculatorAgendarConsulta'
import { ProcedimentoSigtapFieldModel } from '../detail/soap/desfecho/components/ProcedimentoSigtapField'
import { EscutaInicialState, name } from './model'

export const createEscutaInicialCalculator = (procedimentosAutomaticos: Array<Procedimento>) =>
  createDecorator(
    ...createMedicoesEscutaCalculations(name.medicoes),
    ...createProcedimentosAutomaticosCalculations(name.medicoes, procedimentosAutomaticos),
    ...createAgendarConsultaCalculations(name.agendarConsulta)
  )

const createProcedimentosAutomaticosCalculations = (
  medicoes: MetaPath<MedicoesPanelModel>,
  procedimentosAutomaticos: Array<Procedimento>
): Calculation[] => [
  {
    field: [medicoes.peso.absolutePath(), medicoes.altura.absolutePath()],
    updates: {
      [name.procedimentos.absolutePath()]: (
        values: MetaArray<ProcedimentoSigtapFieldModel>,
        allValues: EscutaInicialState,
        prevValues: EscutaInicialState
      ) => {
        const peso = resolveValue(allValues, medicoes.peso)
        const altura = resolveValue(allValues, medicoes.altura)
        let procedToAdd = null
        let listaProcedimentos = null
        procedToAdd =
          peso && altura
            ? (procedToAdd = ProcedimentosAutomaticosEscutaInicial.AVALIACAO_ANTROPOMETRICA)
            : peso
            ? (procedToAdd = ProcedimentosAutomaticosEscutaInicial.MEDICAO_DE_PESO)
            : (procedToAdd = ProcedimentosAutomaticosEscutaInicial.MEDICAO_DE_ALTURA)

        listaProcedimentos = addProcedimentoAutomatico(
          allValues,
          prevValues,
          name.procedimentos,
          procedimentosAutomaticos,
          procedToAdd
        )

        if (!(peso && altura)) {
          listaProcedimentos = removeProcedimentoAutomatico(
            listaProcedimentos,
            ProcedimentosAutomaticosEscutaInicial.AVALIACAO_ANTROPOMETRICA
          )
        }
        if (!peso) {
          listaProcedimentos = removeProcedimentoAutomatico(
            listaProcedimentos,
            ProcedimentosAutomaticosEscutaInicial.MEDICAO_DE_PESO
          )
        }
        if (!altura) {
          listaProcedimentos = removeProcedimentoAutomatico(
            listaProcedimentos,
            ProcedimentosAutomaticosEscutaInicial.MEDICAO_DE_ALTURA
          )
        }
        if (peso && altura) {
          listaProcedimentos = removeProcedimentoAutomatico(
            listaProcedimentos,
            ProcedimentosAutomaticosEscutaInicial.MEDICAO_DE_PESO
          )
          listaProcedimentos = removeProcedimentoAutomatico(
            listaProcedimentos,
            ProcedimentosAutomaticosEscutaInicial.MEDICAO_DE_ALTURA
          )
        }
        return listaProcedimentos
      },
    },
  },
  {
    field: [medicoes.pressaoArterialSistolica.absolutePath(), medicoes.pressaoArterialDiastolica.absolutePath()],
    updates: {
      [name.procedimentos.absolutePath()]: (
        values: MetaArray<ProcedimentoSigtapFieldModel>,
        allValues: EscutaInicialState,
        prevValues: EscutaInicialState
      ) => {
        const listaProcedimentos = addProcedimentoAutomatico(
          allValues,
          prevValues,
          name.procedimentos,
          procedimentosAutomaticos,
          ProcedimentosAutomaticosEscutaInicial.AFERICAO_PRESSAO_ARTERIAL
        )
        return resolveValue(allValues, medicoes.pressaoArterialSistolica) &&
          resolveValue(allValues, medicoes.pressaoArterialDiastolica)
          ? listaProcedimentos
          : removeProcedimentoAutomatico(
              listaProcedimentos,
              ProcedimentosAutomaticosEscutaInicial.AFERICAO_PRESSAO_ARTERIAL
            )
      },
    },
  },
  {
    field: medicoes.glicemia.absolutePath(),
    updates: {
      [name.procedimentos.absolutePath()]: (
        values: MetaArray<ProcedimentoSigtapFieldModel>,
        allValues: EscutaInicialState,
        prevValues: EscutaInicialState
      ) => {
        const listaProcedimentos = addProcedimentoAutomatico(
          allValues,
          prevValues,
          name.procedimentos,
          procedimentosAutomaticos,
          ProcedimentosAutomaticosEscutaInicial.GLICEMIA_CAPILAR
        )
        return resolveValue(allValues, medicoes.glicemia)
          ? listaProcedimentos
          : removeProcedimentoAutomatico(listaProcedimentos, ProcedimentosAutomaticosEscutaInicial.GLICEMIA_CAPILAR)
      },
    },
  },
  {
    field: medicoes.temperatura.absolutePath(),
    updates: {
      [name.procedimentos.absolutePath()]: (
        values: MetaArray<ProcedimentoSigtapFieldModel>,
        allValues: EscutaInicialState,
        prevValues: EscutaInicialState
      ) => {
        const listaProcedimentos = addProcedimentoAutomatico(
          allValues,
          prevValues,
          name.procedimentos,
          procedimentosAutomaticos,
          ProcedimentosAutomaticosEscutaInicial.AFERICAO_DE_TEMPERATURA
        )
        return resolveValue(allValues, medicoes.temperatura)
          ? listaProcedimentos
          : removeProcedimentoAutomatico(
              listaProcedimentos,
              ProcedimentosAutomaticosEscutaInicial.AFERICAO_DE_TEMPERATURA
            )
      },
    },
  },
]

const addProcedimentoAutomatico = (
  allValues: EscutaInicialState,
  prevValues: EscutaInicialState,
  existedProcedimentos: MetaArray<ProcedimentoSigtapFieldModel>,
  procedimentosAutomaticos: Array<Procedimento>,
  procedimentoAutomaticoEnum: ProcedimentosAutomaticosEscutaInicial
) => {
  const procedimento: ProcedimentoSigtapFieldModel = {
    _id: uuidv4(),
    procedimento: procedimentosAutomaticos.find((p) => p.codigo === procedimentoAutomaticoEnum),
    automatico: true,
  }

  const prevValuesNotEmpty = resolveValue(prevValues, existedProcedimentos)
  const values = resolveValue(allValues, existedProcedimentos) || ([] as ProcedimentoSigtapFieldModel[])

  if (Array.isArray(values) && procedimento.procedimento) {
    if (!values.find((p) => p.procedimento.id === procedimento.procedimento.id) && prevValuesNotEmpty) {
      values.push(procedimento)
    }
  }

  return values
}

const removeProcedimentoAutomatico = (
  listaProcedimentos: ProcedimentoSigtapFieldModel | ProcedimentoSigtapFieldModel[],
  procedimentoAutomaticoEnum: ProcedimentosAutomaticosEscutaInicial
) => {
  if (Array.isArray(listaProcedimentos)) {
    const newListaProcedimentos = listaProcedimentos.filter((p) =>
      p.automatico ? p.procedimento.codigo !== procedimentoAutomaticoEnum : p
    )
    return newListaProcedimentos
  }
}
